import { useTranslations } from '@vocab/react';
import { Text } from 'braid-design-system';
import type React from 'react';

import { EmDash } from 'src/constants/table';
import { AnalyticsAdRatingStatus } from 'src/graphql/__generated__';
import type { Job } from 'src/types';

import { EmptyTableCell } from '../EmptyTableCell';
import { Skeleton } from '../Skeleton/Skeleton';

import translations from './.vocab';
import { JobPerformanceView } from './components/JobPerformanceView';

interface JobProps {
  job: Job;
  loading?: boolean;
}

export const JobPerformance: React.FC<React.PropsWithChildren<JobProps>> = ({
  job,
  loading = false,
}) => {
  const { t } = useTranslations(translations);

  if (loading) {
    return (
      <Text>
        <Skeleton loading numberOfCharacters={10} />
      </Text>
    );
  }

  const jobId = job?.jobId;
  const { status, score } = job?.performanceRating || {};
  switch (status) {
    case AnalyticsAdRatingStatus.ARCHIVED:
      return (
        <JobPerformanceView
          jobId={jobId}
          label={t('status-archived-label')}
          tooltip={t('status-archived-tooltip')}
        />
      );

    case AnalyticsAdRatingStatus.PENDING:
      return (
        <JobPerformanceView
          jobId={jobId}
          label={t('status-pending-label')}
          tooltip={t('status-pending-tooltip')}
        />
      );

    case AnalyticsAdRatingStatus.UNAVAILABLE:
      return (
        <JobPerformanceView
          jobId={jobId}
          label={t('status-unavailable-label')}
          tooltip={t('status-unavailable-tooltip')}
        />
      );
    case AnalyticsAdRatingStatus.AVAILABLE_WITH_RATING: {
      // When rating is available then we use the score to determine the label
      switch (score) {
        // removed HIGH | NORMAL | LOW from here as part of experiment
        // to increase click rates to the Ad Performance report
        default:
          return (
            <JobPerformanceView
              jobId={jobId}
              linkText={t('status-available-no-score-label')}
            />
          );
      }
    }
    case AnalyticsAdRatingStatus.AVAILABLE_PENDING_RATING:
      return (
        <JobPerformanceView
          jobId={jobId}
          linkText={t('status-available-no-score-label')}
        />
      );
    case AnalyticsAdRatingStatus.AVAILABLE_CANNOT_RATE:
      return (
        <JobPerformanceView
          jobId={jobId}
          linkText={t('status-available-no-score-label')}
        />
      );
    case AnalyticsAdRatingStatus.WITHHELD:
      return (
        <JobPerformanceView
          jobId={jobId}
          linkText={t('status-available-no-score-label')}
        />
      );

    case AnalyticsAdRatingStatus.NOT_INCLUDED:
      return (
        <JobPerformanceView
          jobId={jobId}
          label={t('status-not-included-label')}
          tooltip={t('status-not-included-tooltip')}
        />
      );

    case AnalyticsAdRatingStatus.ERRORED:
    case AnalyticsAdRatingStatus.NOT_APPLICABLE:
      return (
        <JobPerformanceView jobId={jobId} label={EmDash} textSize="standard" />
      );
    default:
      return <EmptyTableCell />;
  }
};
